/**
 * Chunk Error Boundary
 *
 * Catches the failure of a lazily-imported chunk and recovers by reloading once.
 *
 * Why this is needed: code-split chunks are named with a content hash that changes
 * on every build, and Cloudflare Pages only serves the current deployment's chunks.
 * A tab opened before a deploy still holds the previous chunk names, so a lazy
 * import issued afterwards asks for a file that no longer exists. The SPA fallback
 * answers with index.html instead of a 404, the browser refuses the HTML where it
 * expected a module, and the import rejects — taking the subtree down with it.
 *
 * Reloading pulls the current index.html and its matching chunk names. The time of
 * the attempt is recorded so that an immediate second failure (chunk really missing,
 * offline) renders the fallback instead of looping, while a failure days later — a
 * different deploy — still gets its own reload.
 */

import { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';

const RELOAD_KEY = 'anouk:chunk-reloaded-at';

/** A second failure within this window means reloading did not help — stop trying. */
const RELOAD_COOLDOWN_MS = 30_000;

/**
 * Recognises the various ways browsers report a failed dynamic import.
 *
 * @param error The error thrown while rendering the lazy subtree
 * @returns true when it looks like a stale/missing chunk rather than a bug in the component
 */
function isChunkLoadError(error: Error): boolean {
  const message = `${error.name} ${error.message}`;
  return (
    /Failed to fetch dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /ChunkLoadError/i.test(message) ||
    // Chrome's wording when the SPA fallback returns text/html for a module request
    /Expected a JavaScript(-or-Wasm)? module script/i.test(message)
  );
}

interface Props {
  children: ReactNode;
  /** Rendered when reloading is not an option (already retried, or an unrelated error) */
  fallback: ReactNode;
}

interface State {
  hasError: boolean;
}

export class ChunkErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (!isChunkLoadError(error)) {
      // Not our problem — surface it so it is not silently swallowed
      console.error('[ChunkErrorBoundary] Non-chunk error:', error, errorInfo);
      return;
    }

    const lastReload = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Date.now() - lastReload < RELOAD_COOLDOWN_MS) {
      console.error('[ChunkErrorBoundary] Chunk still failing after a reload:', error);
      return;
    }

    console.warn('[ChunkErrorBoundary] Stale chunk after a deploy — reloading:', error.message);
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    window.location.reload();
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}
